import React, { useCallback, useEffect, useState } from "react";
import { Group, Badge, ActionIcon, Text } from "@mantine/core";
import { MdRefresh } from "react-icons/md";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from "recharts";
import { useMsal } from "@azure/msal-react";

import { getToken, useLogin } from "../../authConfig";
import { getChatMessagesAnalyticsApi, ChatMessageDataPoint } from "../../api";
import {
    AggregationToggleGroup,
    AggregationValue,
    ChartScaffold,
    DateRangeControl,
    CountryFilterControl,
    DomainFilterControl,
    TouchpointFilterControl,
    createDefaultDateRange,
    formatDateRange
} from "./shared";
import { TimezoneIndicator } from "../common/TimezoneIndicator";
import { TimezoneUtils } from "../../utils/timezoneUtils";

export interface AverageChatMessagesChartProps {
    title?: string;
    height?: number;
    showTitle?: boolean;
    showLegend?: boolean;
    showStats?: boolean;
    defaultAggregation?: AggregationValue;
    entraOid?: string; // Filter for specific user
    defaultDaysBack?: number; // Number of days back for default date range
    colors?: {
        average?: string;
    };
}

export const AverageChatMessagesChart: React.FC<AverageChatMessagesChartProps> = ({
    title = "Messages per Chat Analytics",
    height = 400,
    showTitle = true,
    showLegend = true,
    showStats = true,
    defaultAggregation = "day",
    entraOid,
    defaultDaysBack = 7,
    colors = {
        average: "#7c3aed"
    }
}) => {
    const [data, setData] = useState<ChatMessageDataPoint[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [availableCountries, setAvailableCountries] = useState<string[]>([]);
    const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
    const [availableDomains, setAvailableDomains] = useState<string[]>([]);
    const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
    const [availableTouchpoints, setAvailableTouchpoints] = useState<string[]>([]);
    const [selectedTouchpoints, setSelectedTouchpoints] = useState<string[]>([]);
    const [sqlQuery, setSqlQuery] = useState<string | undefined>();

    const client = useLogin ? useMsal().instance : undefined;

    const [aggregation, setAggregation] = useState<AggregationValue>(defaultAggregation);
    const [dateRange, setDateRange] = useState<[Date, Date]>(() => createDefaultDateRange(defaultDaysBack));

    const loadData = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const idToken = client ? await getToken(client) : undefined;
            if (!idToken) {
                throw new Error("No idToken available for fetching analytics");
            }

            const [start, end] = dateRange;
            const userTimezone = TimezoneUtils.getUserTimezone();
            const startDate = TimezoneUtils.formatDateForAPI(start);
            const endDate = TimezoneUtils.formatDateForAPI(end);
            const countries = selectedCountries.length > 0 ? selectedCountries : undefined;
            const domains = selectedDomains.length > 0 ? selectedDomains : undefined;
            const touchpoints = selectedTouchpoints.length > 0 ? selectedTouchpoints : undefined;
            const analytics = await getChatMessagesAnalyticsApi(
                idToken,
                aggregation,
                startDate,
                endDate,
                userTimezone,
                countries,
                touchpoints,
                entraOid,
                domains
            );
            setData(analytics.data);
            setSqlQuery(analytics.sql_query ?? undefined);

            // Update available countries and touchpoints from the analytics response
            if (analytics.available_countries) {
                setAvailableCountries(analytics.available_countries);
            }
            setAvailableDomains(analytics.available_domains || []);
            if (analytics.available_touchpoints) {
                setAvailableTouchpoints(analytics.available_touchpoints);
            }
        } catch (chartError) {
            setError(chartError instanceof Error ? chartError.message : "Failed to load average chat messages data");
            setData([]);
            setSqlQuery(undefined);
        } finally {
            setIsLoading(false);
        }
    }, [aggregation, client, dateRange, selectedCountries, selectedDomains, selectedTouchpoints, entraOid]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const totalConversations = data.reduce((sum, item) => sum + item.total_conversations, 0);
    const totalMessages = data.reduce((sum, item) => sum + item.total_messages, 0);
    const overallAverage = totalConversations > 0 ? (totalMessages / totalConversations).toFixed(2) : "0";

    const stats =
        showStats && totalConversations > 0 ? (
            <Group gap="xs">
                <Badge color="violet" variant="light" size="sm">
                    {overallAverage} Avg Messages/Conversation
                </Badge>
                <Badge color="blue" variant="light" size="sm">
                    {totalConversations} Conversations
                </Badge>
            </Group>
        ) : null;

    const controls = (
        <Group gap={6} style={{ flexWrap: "nowrap" }}>
            <AggregationToggleGroup value={aggregation} onChange={setAggregation} disabled={!!error} />
            <DateRangeControl value={dateRange} onChange={setDateRange} disabled={!!error} />
            <CountryFilterControl
                value={selectedCountries}
                onChange={setSelectedCountries}
                availableCountries={availableCountries}
                disabled={!!error}
                loading={isLoading}
            />
            <DomainFilterControl
                value={selectedDomains}
                onChange={setSelectedDomains}
                availableDomains={availableDomains}
                disabled={!!error}
                loading={isLoading}
            />
            <TouchpointFilterControl
                value={selectedTouchpoints}
                onChange={setSelectedTouchpoints}
                availableTouchpoints={availableTouchpoints}
                disabled={!!error}
                loading={isLoading}
            />
            <ActionIcon variant="light" size="sm" onClick={loadData} loading={isLoading} title="Refresh data" disabled={!!error}>
                <MdRefresh size={16} />
            </ActionIcon>
        </Group>
    );

    // Check if any filters are applied (different from default state)
    const defaultDateRange = createDefaultDateRange(defaultDaysBack);
    const isDateRangeModified = dateRange[0].getTime() !== defaultDateRange[0].getTime() || dateRange[1].getTime() !== defaultDateRange[1].getTime();
    const hasFiltersApplied =
        aggregation !== "day" || isDateRangeModified || selectedCountries.length > 0 || selectedDomains.length > 0 || selectedTouchpoints.length > 0;

    const handleResetFilters = () => {
        setAggregation("day");
        setDateRange(createDefaultDateRange(defaultDaysBack));
        setSelectedCountries([]);
        setSelectedDomains([]);
        setSelectedTouchpoints([]);
    };

    const info = (
        <Group justify="flex-end" align="center" gap="xs">
            <Text size="xs" c="dimmed">
                {formatDateRange(dateRange)}
            </Text>
            <TimezoneIndicator size="xs" />
        </Group>
    );

    return (
        <ChartScaffold
            title={title}
            showTitle={showTitle}
            loading={isLoading}
            error={error}
            empty={!error && data.length === 0}
            emptyMessage="No average message data found for the selected time range."
            stats={stats}
            controls={controls}
            info={info}
            height={height}
            onRefresh={loadData}
            onResetFilters={handleResetFilters}
            showResetFilters={hasFiltersApplied}
            infoContent="Shows the average number of messages per conversation over time. Helps identify engagement trends and conversation complexity patterns."
            sqlQuery={sqlQuery}
            chartData={data}
            showDeveloperTools={true}
        >
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="name"
                        tick={{ fontSize: 12 }}
                        angle={data.length > 10 ? -45 : 0}
                        textAnchor={data.length > 10 ? "end" : "middle"}
                        height={data.length > 10 ? 60 : 30}
                    />
                    <YAxis tick={{ fontSize: 12 }} domain={["dataMin - 0.5", "dataMax + 0.5"]} />
                    <Tooltip
                        formatter={(value, name) => {
                            const label = (name as string) ?? "";
                            const scalar = Array.isArray(value) ? (value as (number | string)[])[0] : (value as number | string);
                            const numericValue = typeof scalar === "number" ? scalar : Number(scalar ?? 0);

                            if (label === "average") {
                                return [numericValue.toFixed(2), "Avg Messages/Conversation"];
                            }
                            if (label === "total_messages") {
                                return [numericValue, "Total Messages"];
                            }
                            return [scalar ?? numericValue, label];
                        }}
                        contentStyle={{ backgroundColor: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: 4 }}
                    />
                    {showLegend && <Legend />}
                    <Line
                        type="monotone"
                        dataKey="average"
                        stroke={colors.average}
                        strokeWidth={3}
                        name="Avg Messages/Conversation"
                        dot={{ fill: colors.average, strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6, stroke: colors.average, strokeWidth: 2 }}
                    >
                        <LabelList
                            dataKey="average"
                            position="top"
                            style={{ fontSize: "12px", fill: "#666" }}
                            formatter={(value: any) => (typeof value === "number" ? value.toFixed(1) : value)}
                        />
                    </Line>
                </LineChart>
            </ResponsiveContainer>
        </ChartScaffold>
    );
};
